import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, ArrowRight, Bell, Mail } from 'lucide-react';

interface StepNotificationsProps {
  defaultEmail: string;
  onComplete: (data: {
    newReservation: boolean;
    dailySummary: boolean;
    sendTime: string;
    recipientEmail: string;
  }) => void;
  onBack: () => void;
  onSkip: () => void;
}

export function StepNotifications({ defaultEmail, onComplete, onBack, onSkip }: StepNotificationsProps) {
  const [newReservation, setNewReservation] = useState(true);
  const [dailySummary, setDailySummary] = useState(true);
  const [sendTime, setSendTime] = useState('07:00');
  const [recipientEmail, setRecipientEmail] = useState(defaultEmail);

  const handleSubmit = () => {
    onComplete({
      newReservation,
      dailySummary,
      sendTime,
      recipientEmail,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Bell className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Notifications</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Restez informé des nouvelles réservations
        </p>
      </div>

      {/* Email info */}
      <div className="p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2 text-sm">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Notifications envoyées à :</span>
          <span className="font-medium">{recipientEmail}</span>
        </div>
      </div>

      {/* New reservation notification */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div className="space-y-0.5">
          <Label htmlFor="new-reservation">Nouvelle réservation</Label>
          <p className="text-sm text-muted-foreground">
            Recevoir un email à chaque nouvelle réservation
          </p>
        </div>
        <Switch
          id="new-reservation"
          checked={newReservation}
          onCheckedChange={setNewReservation}
        />
      </div>

      {/* Daily summary */}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-0.5">
            <Label htmlFor="daily-summary">Récapitulatif quotidien</Label>
            <p className="text-sm text-muted-foreground">
              Recevoir un résumé des réservations du jour
            </p>
          </div>
          <Switch
            id="daily-summary"
            checked={dailySummary}
            onCheckedChange={setDailySummary}
          />
        </div>

        {dailySummary && (
          <div className="pl-4 flex items-center gap-3">
            <Label htmlFor="send-time" className="text-sm text-muted-foreground whitespace-nowrap">
              Heure d'envoi :
            </Label>
            <Input
              id="send-time"
              type="time"
              value={sendTime}
              onChange={(e) => setSendTime(e.target.value)}
              className="w-32"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onBack} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button variant="ghost" onClick={onSkip}>
          Passer
        </Button>
        <Button onClick={handleSubmit} className="flex-1">
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
